import { useState } from 'react';
import { atualizarDispositivo, criarDispositivo } from '../../api/dispositivos.js';
import type { Dispositivo } from '../../api/dispositivos.js';
import { mensagemDoErro } from '../../api/erros.js';
import { CampoTexto } from '../../componentes/Campo.js';
import { FormularioLateral } from '../../componentes/FormularioLateral.js';
import { seHouver } from '../../componentes/props.js';

type Props = {
  /** `null` cadastra; com dispositivo, edita. */
  readonly dispositivo: Dispositivo | null;
  readonly aoFechar: () => void;
  /** Recebe o registro salvo — no cadastro, é dele que sai o pareamento. */
  readonly aoConcluir: (salvo: Dispositivo, cadastrou: boolean) => void;
};

/** Cadastro e edição de aparelho. O pareamento vem logo depois do cadastro. */
export function FormularioDeDispositivo({ dispositivo, aoFechar, aoConcluir }: Props) {
  const [identificador, definirIdentificador] = useState(dispositivo?.identificador ?? '');
  const [apelido, definirApelido] = useState(dispositivo?.apelido ?? '');
  const [erroDoCampo, definirErroDoCampo] = useState<string | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  const editando = dispositivo !== null;

  function confirmar() {
    if (identificador.trim() === '') {
      definirErroDoCampo('Informe o identificador.');
      return;
    }

    definirErroDoCampo(null);
    definirErro(null);
    definirEnviando(true);

    const limpo = apelido.trim();
    const escrita = editando
      ? // `null` limpa o apelido; string vazia não significa nada para a API.
        atualizarDispositivo(dispositivo.id, {
          identificador: identificador.trim(),
          apelido: limpo === '' ? null : limpo,
        })
      : criarDispositivo({
          identificador: identificador.trim(),
          ...(limpo === '' ? {} : { apelido: limpo }),
        });

    escrita
      .then((salvo) => {
        aoConcluir(salvo, !editando);
      })
      .catch((falha: unknown) => {
        definirEnviando(false);
        definirErro(mensagemDoErro(falha));
      });
  }

  return (
    <FormularioLateral
      titulo={editando ? `Editar ${dispositivo.identificador}` : 'Novo dispositivo'}
      subtitulo={
        editando
          ? 'O vínculo com o operador e a fila do aparelho não mudam aqui.'
          : 'Ao cadastrar, o painel já gera o código de pareamento — ele aparece uma única vez.'
      }
      rotuloConfirmar={editando ? 'Salvar' : 'Cadastrar e parear'}
      aoConfirmar={confirmar}
      aoFechar={aoFechar}
      enviando={enviando}
      {...seHouver('erro', erro)}
    >
      <CampoTexto
        rotulo="Identificador"
        valor={identificador}
        aoMudar={definirIdentificador}
        fonte="mono"
        obrigatorio
        autoCompletar="off"
        ajuda="Como o aparelho é chamado no armazém, ex.: COLETOR-DOCA-1."
        {...seHouver('erro', erroDoCampo)}
      />
      <CampoTexto
        rotulo="Apelido"
        valor={apelido}
        aoMudar={definirApelido}
        autoCompletar="off"
        ajuda="Opcional. Onde ele costuma ficar, para quem olha a lista."
      />
    </FormularioLateral>
  );
}
